/**
 * LPMiniStep — True LP Preview Surface inside PPB
 *
 * MA-PPB-LP-034: True LP Preview Parity
 *
 * Two-panel layout:
 *   Left 35%:  Sections list (selectable, scroll-to-section) + action buttons
 *   Right 65%: True LP preview canvas reusing LPSectionPreview.jsx renderers
 *
 * StepHeader (with Next button) is rendered by LandingPageStepPanel in PPB.
 *
 * Architecture: Reuses the SAME rendering pipeline as LP Builder Preview mode:
 *   - renderPreviewSection() for all section rendering
 *   - hasPreviewContent() for section suppression
 *   - getSurfaceConfig() for surface/background system
 *   - setActiveBrand() for token resolution
 *   - Same device toggle (desktop/mobile) with identical width constraints
 *
 * Props:
 *   draft         — PPB draft state
 *   onFieldChange — PPB field change handler
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Layers, Sparkles, Link2, Copy, ExternalLink, Loader2,
  AlertTriangle, RefreshCw, FileText, Globe,
  MousePointerClick, Zap, Shield, HelpCircle, Target,
  ListTree, Megaphone, Monitor, Smartphone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { buildStrategyContext } from "@/lib/strategyContext";
import { LPAssetSelector } from "./LPAssetSelector";
import { normalizeSnapshot } from "@/lib/normalizeSnapshot";
import {
  renderPreviewSection,
  hasPreviewContent,
  setActiveBrand,
  getActiveTokens,
  getSurfaceConfig,
} from "@/components/lp-builder/LPSectionPreview";
import { extractLPTheme } from "@/lib/resolveThemeSource";

// MA-LP-PROD-030: Order matches SECTION_ORDER from normalizeSnapshot.js exactly.
// funnel_embed added. what_this_is_not marked as consumed-by what_this_is.
const LP_SECTIONS = [
  { key: "hero",                label: "Hero",               icon: Megaphone },
  { key: "trust_signals",       label: "Trust Signals",      icon: Shield },
  { key: "funnel_embed",        label: "Funnel Embed",       icon: Zap },
  { key: "how_it_works",        label: "How It Works",       icon: ListTree },
  { key: "personal_authority",  label: "Personal Authority", icon: Shield },
  { key: "mid_cta",             label: "Mid CTA",            icon: MousePointerClick },
  { key: "who_this_is_for",     label: "Who This Is For",    icon: Target },
  { key: "who_this_is_not_for", label: "Who This Is NOT For",icon: Target },
  { key: "what_happens_next",   label: "What Happens Next",  icon: Zap },
  { key: "what_this_is",        label: "What This Is",       icon: FileText },
  { key: "what_this_is_not",    label: "What This Is Not",   icon: FileText, consumedBy: "what_this_is" },
  { key: "faq",                 label: "FAQ",                icon: HelpCircle },
  { key: "final_cta",           label: "Final CTA",          icon: MousePointerClick },
  { key: "seo",                 label: "SEO & Meta",         icon: Globe },
];

const STATUS_CONFIG = {
  draft:     { label: "Draft",     color: "bg-slate-500/20 text-slate-300 border-slate-500/30" },
  active:    { label: "Active",    color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  published: { label: "Published", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  archived:  { label: "Archived",  color: "bg-amber-500/20 text-amber-300 border-amber-500/30" },
};

const LINK_MODE_LABELS = {
  generated_new: "Generated",
  linked_existing: "Linked",
  duplicated: "Duplicated",
};

// ── Shared constants for cross-tab sync ──
const SYNC_CHANNEL_KEY = "ppb_lp_sync";

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════

export default function LPMiniStep({ draft, onFieldChange, onLPContextChanged, resolvedLpTokens }) {
  const [lpContext, setLpContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorMode, setSelectorMode] = useState("link");
  const [selectedSectionIdx, setSelectedSectionIdx] = useState(0);
  const [deviceMode, setDeviceMode] = useState("desktop");
  const previewScrollRef = useRef(null);

  const profitPathId = draft?.id;
  const tenantId = draft?.tenant_id;

  // Upstream dependencies
  const concepts = draft?.builder_data?.creative?.concepts || [];
  const selectedConcept = concepts.find((c) => c.is_selected);
  const funnelSteps = draft?.builder_data?.funnel?.steps || [];
  const hasFunnel = funnelSteps.length >= 3 || !!draft?.builder_data?.funnel?.funnel_instance_id;
  const canGenerate = !!selectedConcept;

  const strategyCtx = useMemo(() => buildStrategyContext(draft), [draft]);

  // ── Fetch linked LP context ──
  const fetchLpContext = useCallback(async () => {
    if (!profitPathId) return;
    setLoading(true);
    try {
      const res = await api.get(`/landing-pages/instance-for-profit-path/${profitPathId}`);
      setLpContext(res.data);
    } catch {
      setLpContext({ linked: false });
    } finally {
      setLoading(false);
    }
  }, [profitPathId]);

  useEffect(() => { fetchLpContext(); }, [fetchLpContext]);

  // MA-LP-PROD-029 P2-5: Push LP context up to parent (ProfitPathBuilder)
  // so alignment/completion checks read from LP API, not builder_data.
  useEffect(() => {
    if (onLPContextChanged) onLPContextChanged(lpContext);
  }, [lpContext, onLPContextChanged]);

  // ── MA-PPB-LP-034D: Cross-tab sync — window focus + storage event ──
  useEffect(() => {
    const handleFocus = () => { fetchLpContext(); };
    const handleStorage = (e) => {
      if (e.key === SYNC_CHANNEL_KEY) fetchLpContext();
    };
    window.addEventListener("focus", handleFocus);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("storage", handleStorage);
    };
  }, [fetchLpContext]);

  const lp = lpContext?.landing_page;
  const asset = lpContext?.asset;
  const instance = lpContext?.instance;
  const currentVersion = lpContext?.current_version;
  const draftVersion = lpContext?.draft_version;
  const versionCount = lpContext?.version_count || 0;
  const isLinked = lpContext?.linked === true;
  const lpId = lpContext?.landing_page_id;

  const meta = asset?.metadata || lp?.metadata || {};
  const linkMode = instance?.link_mode;

  // Staleness check
  const knowledgeHash = draft?.builder_data?.knowledge_hash;
  const lpKnowledgeHash = currentVersion?.snapshot_json?.knowledge_hash;
  const isStale = knowledgeHash && lpKnowledgeHash && knowledgeHash !== lpKnowledgeHash;

  // ── Derive sections with data from version snapshot (Phase 2: normalize) ──
  const normalizedSections = useMemo(() => {
    return normalizeSnapshot(currentVersion?.snapshot_json || {});
  }, [currentVersion]);

  const snapshotUsable = normalizedSections.length > 0;

  // MA-LP-THEME-037: Single source of token resolution — use tenant_brand_config
  // from the API response as the authoritative brand source (same as LP Builder's /api/brand-config).
  // MA-THEME-OVERRIDE-097: PRIORITY 1 - Use Profit Path theme if available (PPB context)
  // Priority: Profit Path resolvedLpTokens > lp.metadata.brand_config > version.snapshot > tenant > DEFAULT
  useEffect(() => {
    let theme;
    
    console.log("═══════════════════════════════════════════════════");
    console.log("🎨 LP PREVIEW THEME RESOLUTION (MA-THEME-OVERRIDE-097)");
    console.log("Priority 1: Profit Path resolvedLpTokens?", !!resolvedLpTokens?.accent);
    if (resolvedLpTokens?.accent) {
      console.log("  accent:", resolvedLpTokens.accent);
      console.log("  surfaceMode:", resolvedLpTokens.surfaceMode);
    }
    
    // PRIORITY 1: Profit Path theme (from PPB context)
    if (resolvedLpTokens?.accent) {
      theme = {
        accent: resolvedLpTokens.accent,
        surface_mode: resolvedLpTokens.surfaceMode || 'light',
      };
      console.log("✅ Using Profit Path Theme");
    } else {
      // PRIORITY 2-4: LP metadata > version snapshot > tenant brand
      theme = extractLPTheme({
        lp,
        version: currentVersion,
        apiBrandConfig: lpContext?.tenant_brand_config,
      });
      console.log("⚠️  Using LP/Brand Theme (fallback)");
    }
    
    console.log("Final LP theme:", theme);
    console.log("═══════════════════════════════════════════════════");
    
    setActiveBrand(theme);
  }, [resolvedLpTokens, lp, currentVersion, lpContext?.tenant_brand_config]);

  const activeSections = useMemo(() => {
    return normalizedSections.map((s) => {
      const def = LP_SECTIONS.find((ls) => ls.key === s.type);
      return {
        key: s.type,
        label: def?.label || s.type,
        icon: def?.icon || FileText,
        data: s.data,
      };
    });
  }, [normalizedSections]);

  // Build allSectionData map for cross-section references (e.g., what_this_is + what_this_is_not)
  const allSectionData = useMemo(() => {
    return Object.fromEntries(activeSections.map((s) => [s.key, s.data]));
  }, [activeSections]);

  // Sections that have renderable preview content (suppresses empty sections)
  const renderableSections = useMemo(() => {
    return activeSections.filter((s) => hasPreviewContent(s.key, s.data));
  }, [activeSections]);

  const selectedSection = activeSections[selectedSectionIdx] || activeSections[0];

  // ── Scroll to section in preview ──
  const scrollToSection = useCallback((sectionKey) => {
    const el = previewScrollRef.current?.querySelector?.(`[data-section="${sectionKey}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleSectionClick = useCallback((index) => {
    setSelectedSectionIdx(index);
    const section = activeSections[index];
    if (section) {
      // Small delay to allow state update before scrolling
      setTimeout(() => scrollToSection(section.key), 50);
    }
  }, [activeSections, scrollToSection]);

  // ── Generate New ──
  const handleGenerate = async () => {
    if (!canGenerate || isGenerating) return;
    setIsGenerating(true);
    try {
      const messaging = draft?.builder_data?.messaging || {};
      const res = await api.post("/ai/generate-landing-page", {
        profit_path_id: profitPathId,
        strategy_context: strategyCtx,
        messaging,
        step_params: {
          profit_path_id: profitPathId,
          selected_concept: selectedConcept,
          funnel: { steps: funnelSteps },
        },
      });

      const data = res.data;
      if (data.success) {
        // MA-LP-GENERATION-ATOMIC-FIX: Backend now handles linking atomically
        // No separate link call needed - generation endpoint links LP to PP automatically

        // MA-LP-PROD-029 P2-5: LP content is stored in the LP entity (via API).
        // Do NOT write to builder_data.landing_page — LP API is the single source of truth.

        toast({ title: "Landing Page Generated", description: "New LP asset created and linked." });
        setSelectedSectionIdx(0);
        await fetchLpContext();
      }
    } catch (e) {
      toast({
        title: "Generation Failed",
        description: e.response?.data?.detail || "Failed to generate landing page",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Link Existing ──
  const handleLinkExisting = async (selectedLp) => {
    setSelectorOpen(false);
    try {
      const instRes = await api.post(`/assets/${selectedLp.id}/instances?tenant_id=${tenantId}`, {
        asset_type: "landing_page",
        asset_id: selectedLp.id,
        profit_path_id: profitPathId,
        link_mode: "linked_existing",
      });
      const instanceId = instRes.data?.id;
      if (instanceId) {
        await api.post(`/landing-pages/link-to-profit-path/${profitPathId}`, {
          instance_id: instanceId,
        });
      }
      toast({ title: "Landing Page Linked", description: `"${selectedLp.name}" linked to this Profit Path.` });
      setSelectedSectionIdx(0);
      await fetchLpContext();
    } catch (e) {
      toast({
        title: "Link Failed",
        description: e.response?.data?.detail || "Failed to link landing page",
        variant: "destructive",
      });
    }
  };

  // ── Duplicate Existing ──
  const handleDuplicate = async (selectedLp) => {
    setSelectorOpen(false);
    try {
      const dupRes = await api.post(`/assets/${selectedLp.id}/duplicate?tenant_id=${tenantId}`, {
        new_name: `${selectedLp.name} (Copy)`,
        profit_path_id: profitPathId,
      });
      const newInstanceId = dupRes.data?.new_instance?.id;
      if (newInstanceId) {
        await api.post(`/landing-pages/link-to-profit-path/${profitPathId}`, {
          instance_id: newInstanceId,
        });
      }
      toast({ title: "Landing Page Duplicated", description: `Copy of "${selectedLp.name}" created and linked.` });
      setSelectedSectionIdx(0);
      await fetchLpContext();
    } catch (e) {
      toast({
        title: "Duplicate Failed",
        description: e.response?.data?.detail || "Failed to duplicate landing page",
        variant: "destructive",
      });
    }
  };

  // ── Edit in Builder — opens in NEW TAB (MA-PPB-LP-034C) ──
  const handleEditInBuilder = () => {
    if (!lpId) return;
    const params = profitPathId ? `?from_profit_path=${profitPathId}` : "";
    const url = `/app/landing-pages/${lpId}${params}`;
    window.open(url, "_blank");
  };

  // ── Loading state — Show skeleton while fetching LP context ──
  if (loading) {
    return (
      <div className="flex-1 flex gap-3 px-4 pb-4 pt-3 min-h-0 overflow-hidden" data-testid="lp-mini-step-loading">
        {/* Left: Sections list skeleton */}
        <div className="w-[35%] flex flex-col min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] opacity-50 pointer-events-none select-none">
          <div className="flex-1 overflow-y-auto p-3 space-y-3 animate-pulse">
            {/* Section header skeleton */}
            <div className="h-3 w-24 bg-slate-700/40 rounded"></div>
            
            {/* Section items skeleton */}
            <div className="space-y-1">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-full p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded bg-slate-700/40 flex-shrink-0"></div>
                  <div className="h-3.5 w-3.5 bg-slate-700/30 rounded flex-shrink-0"></div>
                  <div className="h-3 bg-slate-700/40 rounded flex-1"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Action buttons skeleton */}
          <div className="flex-shrink-0 p-3 border-t border-white/[0.06] space-y-2">
            <div className="w-full h-8 bg-slate-700/30 rounded"></div>
            <div className="w-full h-8 bg-slate-700/20 rounded"></div>
            <div className="w-full h-8 bg-slate-700/20 rounded"></div>
            <div className="w-full h-8 bg-slate-700/20 rounded"></div>
          </div>
        </div>

        {/* Right: LP Preview skeleton */}
        <div className="w-[65%] min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] overflow-hidden flex flex-col opacity-50 pointer-events-none select-none">
          {/* Preview header skeleton */}
          <div className="px-3 py-2 border-b border-white/[0.06] flex-shrink-0 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-3.5 w-3.5 bg-slate-700/40 rounded"></div>
                <div className="h-3 w-20 bg-slate-700/40 rounded"></div>
                <div className="h-2.5 w-16 bg-slate-700/30 rounded"></div>
              </div>
              <div className="w-16 h-6 bg-slate-700/30 rounded-md"></div>
            </div>
          </div>

          {/* LP canvas skeleton */}
          <div className="flex-1 p-6 space-y-4 overflow-hidden animate-pulse">
            {/* Hero section skeleton */}
            <div className="space-y-3 pb-4 border-b border-slate-700/20">
              <div className="h-6 w-3/4 bg-slate-700/40 rounded mx-auto"></div>
              <div className="h-4 w-2/3 bg-slate-700/30 rounded mx-auto"></div>
              <div className="h-4 w-1/2 bg-slate-700/30 rounded mx-auto"></div>
              <div className="h-10 w-40 bg-slate-700/40 rounded mx-auto mt-4"></div>
            </div>

            {/* Content sections skeleton */}
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2 py-3 border-b border-slate-700/20">
                <div className="h-4 w-1/3 bg-slate-700/40 rounded"></div>
                <div className="h-3 w-full bg-slate-700/30 rounded"></div>
                <div className="h-3 w-5/6 bg-slate-700/30 rounded"></div>
                <div className="h-3 w-4/5 bg-slate-700/30 rounded"></div>
              </div>
            ))}
          </div>

          {/* Loading text */}
          <div className="flex-shrink-0 p-3 border-t border-white/[0.06]">
            <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading landing page...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EMPTY STATE — No LP linked: preserve two-card layout
  // ═══════════════════════════════════════════════════════════════════════════
  if (!isLinked) {
    return (
      <>
        <div className="flex-1 flex gap-3 px-4 pb-4 pt-3 min-h-0 overflow-hidden" data-testid="lp-mini-step-empty">
          {/* Left: Empty sections card */}
          <div className="w-[35%] flex flex-col min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03]">
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Sections (0)</p>
              <div className="text-center py-8">
                <Layers className="w-6 h-6 text-slate-600 mx-auto mb-2" />
                <p className="text-[11px] text-slate-600">No sections yet</p>
              </div>
            </div>

            {/* Action buttons — bottom */}
            <div className="flex-shrink-0 p-3 border-t border-white/[0.06] space-y-2">
              <Button
                data-testid="lp-generate-new-btn"
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating}
                variant="outline"
                className="w-full h-8 text-xs border-indigo-500/30 text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/10"
              >
                {isGenerating ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1.5" />}
                Generate New
              </Button>
              <Button
                data-testid="lp-link-existing-btn"
                variant="outline"
                onClick={() => { setSelectorMode("link"); setSelectorOpen(true); }}
                className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
              >
                <Link2 className="h-3 w-3 mr-1.5" />
                Link Existing
              </Button>
              <Button
                data-testid="lp-duplicate-btn"
                variant="outline"
                onClick={() => { setSelectorMode("duplicate"); setSelectorOpen(true); }}
                className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
              >
                <Copy className="h-3 w-3 mr-1.5" />
                Duplicate Existing
              </Button>
              {!canGenerate && (
                <p className="text-[10px] text-slate-600 text-center pt-1">
                  {!selectedConcept ? "Select a creative concept first." : ""}
                </p>
              )}
              {canGenerate && !hasFunnel && (
                <p className="text-[10px] text-amber-600/70 text-center pt-1">
                  No funnel detected — LP will generate without funnel context.
                </p>
              )}
            </div>
          </div>

          {/* Right: Empty preview card */}
          <div className="w-[65%] min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] overflow-hidden flex flex-col">
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <Layers className="w-10 h-10 text-indigo-400/30 mb-4" />
              <h3 className="text-sm font-semibold text-white mb-1.5">No Landing Page Linked</h3>
              <p className="text-xs text-slate-500 max-w-xs mb-5">
                Generate a new landing page from your strategy, link an existing one, or duplicate.
              </p>
              <Button
                data-testid="lp-generate-primary-cta"
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating}
                className="bg-indigo-600 hover:bg-indigo-500 text-white gap-2 h-9 text-xs"
              >
                {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate New LP
              </Button>
            </div>
          </div>
        </div>

        <LPAssetSelector
          open={selectorOpen}
          onClose={() => setSelectorOpen(false)}
          onSelect={selectorMode === "link" ? handleLinkExisting : handleDuplicate}
          mode={selectorMode}
          tenantId={tenantId}
        />
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LINKED STATE — Two-card mini-builder
  // ═══════════════════════════════════════════════════════════════════════════
  const statusCfg = STATUS_CONFIG[lp?.status] || STATUS_CONFIG.draft;

  return (
    <>
      {/* Instance bar / staleness / header badges */}
      <div className="px-4 pt-2 pb-0 flex-shrink-0 space-y-2" data-testid="lp-mini-step">
        {/* Asset summary strip */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="lp-asset-summary">
          <Layers className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
          <span className="text-xs font-medium text-white truncate max-w-[200px]">{lp?.name || "Landing Page"}</span>
          <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0 h-4", statusCfg.color)}>
            {statusCfg.label}
          </Badge>
          {linkMode && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-white/5 text-slate-400 border-white/10">
              {LINK_MODE_LABELS[linkMode] || linkMode}
            </Badge>
          )}
          {meta.primary_use_case && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-indigo-500/10 text-indigo-300 border-indigo-500/20">
              {meta.primary_use_case.replace(/_/g, " ")}
            </Badge>
          )}
          {currentVersion && (
            <span className="text-[10px] text-slate-500 ml-auto">
              v{currentVersion.version_number} · {versionCount} version{versionCount !== 1 ? "s" : ""}
              {draftVersion && <span className="text-amber-400/70 ml-1">(draft)</span>}
            </span>
          )}
        </div>

        {/* Staleness warning */}
        {isStale && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/8 border border-amber-500/15" data-testid="lp-stale-warning">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
            <span className="text-[11px] text-amber-300">Knowledge updated — regenerate recommended</span>
            <Button
              size="sm" variant="ghost"
              onClick={handleGenerate}
              disabled={!canGenerate || isGenerating}
              className="ml-auto text-[10px] text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-6 px-2"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
            </Button>
          </div>
        )}

        {/* MA-LP-ARCHIVE-014: Archived LP warning */}
        {lpContext?.is_archived && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/8 border border-red-500/20" data-testid="lp-archived-warning">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
            <span className="text-[11px] text-red-300 font-medium">This landing page is archived.</span>
            <span className="text-[10px] text-red-400/70">It is no longer served or active. Link a different LP or generate a new one.</span>
          </div>
        )}
      </div>

      {/* Two-Panel: Sections List (left) + Preview Canvas (right) */}
      <div className="flex-1 flex gap-3 px-4 pb-4 pt-1 min-h-0 overflow-hidden">
        {/* MA-PPB-LOADING-SKELETON: Show loading skeleton during regeneration */}
        {isGenerating ? (
          <>
            {/* Left: Sections list skeleton */}
            <div className="w-[35%] flex flex-col min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] opacity-50 pointer-events-none select-none">
              <div className="flex-1 overflow-y-auto p-3 space-y-3 animate-pulse">
                {/* Section header skeleton */}
                <div className="h-3 w-24 bg-slate-700/40 rounded"></div>
                
                {/* Section items skeleton */}
                <div className="space-y-1">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="w-full p-2 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center gap-2.5">
                      <div className="w-5 h-5 rounded bg-slate-700/40 flex-shrink-0"></div>
                      <div className="h-3.5 w-3.5 bg-slate-700/30 rounded flex-shrink-0"></div>
                      <div className="h-3 bg-slate-700/40 rounded flex-1"></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action buttons skeleton */}
              <div className="flex-shrink-0 p-3 border-t border-white/[0.06] space-y-2">
                <div className="w-full h-8 bg-slate-700/30 rounded"></div>
                <div className="w-full h-8 bg-slate-700/20 rounded"></div>
                <div className="w-full h-8 bg-slate-700/20 rounded"></div>
                <div className="w-full h-8 bg-slate-700/20 rounded"></div>
              </div>
            </div>

            {/* Right: LP Preview skeleton */}
            <div className="w-[65%] min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] overflow-hidden flex flex-col opacity-50 pointer-events-none select-none">
              {/* Preview header skeleton */}
              <div className="px-3 py-2 border-b border-white/[0.06] flex-shrink-0 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3.5 w-3.5 bg-slate-700/40 rounded"></div>
                    <div className="h-3 w-20 bg-slate-700/40 rounded"></div>
                    <div className="h-2.5 w-16 bg-slate-700/30 rounded"></div>
                  </div>
                  <div className="w-16 h-6 bg-slate-700/30 rounded-md"></div>
                </div>
              </div>

              {/* LP canvas skeleton */}
              <div className="flex-1 p-6 space-y-4 overflow-hidden animate-pulse">
                {/* Hero section skeleton */}
                <div className="space-y-3 pb-4 border-b border-slate-700/20">
                  <div className="h-6 w-3/4 bg-slate-700/40 rounded mx-auto"></div>
                  <div className="h-4 w-2/3 bg-slate-700/30 rounded mx-auto"></div>
                  <div className="h-4 w-1/2 bg-slate-700/30 rounded mx-auto"></div>
                  <div className="h-10 w-40 bg-slate-700/40 rounded mx-auto mt-4"></div>
                </div>

                {/* Content sections skeleton */}
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-2 py-3 border-b border-slate-700/20">
                    <div className="h-4 w-1/3 bg-slate-700/40 rounded"></div>
                    <div className="h-3 w-full bg-slate-700/30 rounded"></div>
                    <div className="h-3 w-5/6 bg-slate-700/30 rounded"></div>
                    <div className="h-3 w-4/5 bg-slate-700/30 rounded"></div>
                  </div>
                ))}
              </div>

              {/* Loading text */}
              <div className="flex-shrink-0 p-3 border-t border-white/[0.06]">
                <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Regenerating landing page...
                </p>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Left: Sections card */}
            <div className="w-[35%] flex flex-col min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03]">
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {activeSections.length === 0 ? (
                  <>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Sections (0)</p>
                    <div className="text-center py-8 text-xs text-slate-500">
                      No sections found. Try regenerating.
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">
                      Sections ({activeSections.length})
                    </p>
                    <div className="space-y-1">
                        {activeSections.map((section, index) => {
                        const SIcon = section.icon;
                        const isRenderable = hasPreviewContent(section.key, section.data);
                        return (
                          <button
                            key={section.key}
                            onClick={() => handleSectionClick(index)}
                            className={cn(
                              "w-full p-2 rounded-lg text-left transition-all flex items-center gap-2.5",
                              selectedSectionIdx === index
                                ? "bg-white/[0.08] border border-indigo-500/40 shadow-sm"
                                : "bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] hover:border-white/[0.1]",
                              !isRenderable && "opacity-40"
                            )}
                            data-testid={`lp-section-${section.key}`}
                          >
                            <span className={cn(
                              "w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center flex-shrink-0",
                              selectedSectionIdx === index ? "bg-indigo-500/25 text-indigo-300" : "bg-white/[0.06] text-slate-500"
                            )}>{index + 1}</span>
                            <SIcon className={cn(
                              "h-3.5 w-3.5 flex-shrink-0",
                              selectedSectionIdx === index ? "text-indigo-400" : "text-slate-500"
                            )} />
                            <span className={cn(
                              "text-xs truncate flex-1",
                              selectedSectionIdx === index ? "text-slate-200 font-medium" : "text-slate-400"
                            )}>{section.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Action buttons — anchored to bottom */}
              <div className="flex-shrink-0 p-3 border-t border-white/[0.06] space-y-2" data-testid="lp-action-bar">
                <Button
                  data-testid="lp-regenerate-btn"
                  onClick={handleGenerate}
                  disabled={!canGenerate || isGenerating}
                  variant="outline"
                  className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                >
                  {isGenerating ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1.5" />}
                  Regenerate
                </Button>
                <Button
                  data-testid="lp-edit-in-builder-btn"
                  variant="outline"
                  onClick={handleEditInBuilder}
                  className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                >
                  <ExternalLink className="h-3 w-3 mr-1.5" /> Edit in LP Builder
                </Button>
                <Button
                  data-testid="lp-link-other-btn"
                  variant="outline"
                  onClick={() => { setSelectorMode("link"); setSelectorOpen(true); }}
                  className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                >
                  <Link2 className="h-3 w-3 mr-1.5" /> Link Other
                </Button>
                <Button
                  data-testid="lp-duplicate-linked-btn"
                  variant="outline"
                  onClick={() => { setSelectorMode("duplicate"); setSelectorOpen(true); }}
                  className="w-full h-8 text-xs border-white/[0.08] text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
                >
                  <Copy className="h-3 w-3 mr-1.5" /> Duplicate
                </Button>
              </div>
            </div>

            {/* Right: True LP Preview Canvas — MA-PPB-LP-034A */}
            <div className="w-[65%] min-h-0 rounded-xl border border-white/[0.06] bg-white/[0.03] overflow-hidden flex flex-col" data-testid="lp-preview-canvas">
              {/* Preview header with device toggle */}
              <div className="px-3 py-2 border-b border-white/[0.06] flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="h-3.5 w-3.5 text-indigo-400" />
                    <span className="text-[11px] font-medium text-slate-300">LP Preview</span>
                    <span className="text-[10px] text-slate-600">
                      {renderableSections.length} section{renderableSections.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {/* Desktop / Mobile toggle — same UX as LP Builder */}
                  <div className="inline-flex h-6 items-center gap-0.5 bg-white/[0.06] rounded-md p-0.5" data-testid="ppb-device-toggle">
                    <button
                      onClick={() => setDeviceMode("desktop")}
                      className={cn(
                        "h-5 px-1.5 rounded-sm transition-colors",
                        deviceMode === "desktop" ? "bg-white/[0.1] text-white" : "text-slate-500 hover:text-slate-300"
                      )}
                      data-testid="ppb-preview-desktop-toggle"
                    >
                      <Monitor className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setDeviceMode("mobile")}
                      className={cn(
                        "h-5 px-1.5 rounded-sm transition-colors",
                        deviceMode === "mobile" ? "bg-white/[0.1] text-white" : "text-slate-500 hover:text-slate-300"
                      )}
                      data-testid="ppb-preview-mobile-toggle"
                    >
                      <Smartphone className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Preview body — continuous LP rendering */}
              {renderableSections.length > 0 ? (
                <ScrollArea className="flex-1">
                  <div
                    ref={previewScrollRef}
                    className={cn(
                      "mx-auto transition-all duration-300",
                      deviceMode === "mobile" ? "max-w-[375px]" : "max-w-full"
                    )}
                    style={{ background: getActiveTokens().bgPage }}
                    data-testid="ppb-lp-preview-body"
                  >
                    {activeSections.map((section, index) => {
                      if (!hasPreviewContent(section.key, section.data)) return null;
                      const surfaceBg = getSurfaceConfig()[section.key]?.bg;
                      return (
                        <div
                          key={section.key}
                          onClick={() => setSelectedSectionIdx(index)}
                          style={surfaceBg ? { background: surfaceBg, padding: "40px 20px" } : undefined}
                          className={cn(
                            "cursor-pointer transition-all duration-200 relative",
                            selectedSectionIdx === index
                              ? "ring-1 ring-indigo-400/30 ring-inset"
                              : "hover:ring-1 hover:ring-white/[0.06] hover:ring-inset"
                          )}
                          data-testid={`ppb-preview-${section.key}`}
                        >
                          {renderPreviewSection(section.key, section.data, allSectionData)}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <Layers className="w-8 h-8 text-indigo-400/20 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">No renderable sections</p>
                    <p className="text-[10px] text-slate-600 mt-1">Try regenerating the LP</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Asset Selector Modal */}
      <LPAssetSelector
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        onSelect={selectorMode === "link" ? handleLinkExisting : handleDuplicate}
        mode={selectorMode}
        tenantId={tenantId}
      />
    </>
  );
}
